import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AuthenticationErrorMessage from '../../components/AuthenticationErrorMessage/AuthenticationErrorMessage';
import { PARAM_AUTH_REQ_ID, PARAM_CODE, PARAM_STATE } from '../../components/AuthenticationWrapper';
import ConsentField from '../../components/ConsentField/ConsentField';
import Description from '../../components/Description/Description';
import { ErrorMessage } from '../../components/ErrorMessage';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { extractGovernmentIdFromSubClaim } from '../../helpers/jwt';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useGetConsentService from '../../hooks/useGetConsentService';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import { getAccessToken, initAuthentication, selectValidAccessToken } from '../../store/authentication';
import { setGovernmentIdPageIndex, WizardPageAction } from '../../store/wizard/wizard';
import { AttributeNames } from '../../types/AttributeNames';
import { CountryCode } from '../../types/CountryCode';

const COAPPLICANT_CREDIT_STATUS_CONSENT = 'coapplicantCreditStatusConsent';

/**
 * The DK co-applicant's eSkat/KreditStatus consent step -- the DK counterpart to NO's
 * `CoApplicantConsolidationPageNO` (Gjeldsregisteret), which has no Danish equivalent. The co-applicant's
 * consent is genuinely its own: DK asks each applicant separately, and the contract declares
 * `coapplicantCreditStatusConsent` alongside `applicantCreditStatusConsent`.
 *
 * Mirrors the main-applicant `EskatKreditStatusConsentPageDK` in full, including driving its own MitID login:
 * a co-applicant arrives from the invite URL anonymous, exactly like the main applicant arrives anonymous at
 * its equivalent page. `SubmitCoApplicantWizardDK` renders this page (and `CoApplicantHowToApplyPage`)
 * *outside* any `Authenticator` while unauthenticated -- the same way `SubmitApplicationWizardDK` leaves its
 * first two pages unwrapped -- so the co-applicant can read both before MitID ever runs, matching
 * `SubmitCoApplicantWizardNO`'s `!accessToken` gate (`CoApplicantHowToApplyPage` + `CoApplicantLogInPage`).
 * This page is DK's counterpart to NO's `CoApplicantLogInPage` in that specific sense: it is what actually
 * triggers the login, via `initAuthentication()` on the `mitid-login` footer action, rather than relying on
 * `Authenticator autoLogin`.
 *
 * Copy is reused from the main-applicant page's keys rather than new `coapplicant`-specific ones. All of them
 * exist in Phrase today and read correctly in the second person; a key that only exists in this file would
 * render as its own raw name on screen (`src/api/translation.ts` falls back to the key). New DA copy for the
 * co-applicant flow is LP-2866.
 */
const CoApplicantEskatKreditStatusConsentPageDK = () => {
    useComponentTracker('CoApplicantEskatKreditStatusConsentPageDK');

    const translate = useTranslation();
    const dispatch = useAppDispatch();
    const { coApplicantTotalSections, coApplicantTotalAboutYouPages } = usePagesConfig();
    const { currentPage, pageAction, numberOfPages } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();
    const [params, setParams] = useSearchParams();
    useSetBannerType('bannerOne');

    // Deliberately the real access token only, matching VerifyIncomePageNO/CoApplicantLogInPage/the
    // main-applicant EskatKreditStatusConsentPageDK, which perform their own real authentication exchange
    // even while demo mode is active elsewhere in the app.
    const accessToken = useAppSelector(selectValidAccessToken);
    const coapplicantGovernmentIdFieldState = useAppSelector(selectFieldStateByName)(
        AttributeNames.coapplicantGovernmentId,
    );
    // Only set by this page's own `mitid-login` action below, never by anything upstream (e.g. the generic
    // login form `Authenticator` shows on `/ongoing` when this page is reached there instead of via
    // `/coapplicant`'s own login-gated entry). Required for auto-advance below so a token obtained through
    // that other login path can't be mistaken for "consent was already given here" (LP-2851).
    const coapplicantCreditStatusConsentFieldState = useAppSelector(selectFieldStateByName)(
        COAPPLICANT_CREDIT_STATUS_CONSENT,
    );
    // Only read to recompute (not blindly trust) an existing coapplicantGovernmentId error below -- see the
    // extraction effect's comment. Populated in this session only if CoApplicantPage's own state was ever
    // inherited (LP-2980); in the normal, fresh co-applicant session it's simply undefined.
    const applicantGovernmentIdFieldState = useAppSelector(selectFieldStateByName)(
        AttributeNames.applicantGovernmentId,
    );
    const [error, setError] = useState<boolean>(false);

    useEffect(() => {
        if (accessToken) {
            return;
        }
        if (params.get(PARAM_CODE) && params.get(PARAM_STATE)) {
            dispatch(getAccessToken({ state: params.get(PARAM_STATE)!, code: params.get(PARAM_CODE)! }));
            params.delete(PARAM_CODE);
            params.delete(PARAM_STATE);
            params.delete(PARAM_AUTH_REQ_ID);
            setParams(params);
        }
    }, [params, accessToken]);

    useEffect(() => {
        // Record where this page sits in the current wizard so CoApplicantSubmitPageDK can route back here if
        // coapplicantGovernmentId ends up missing at submit, without hard-coding an index -- mirrors
        // VerifyIncomePageNO's identical use of this field for NO/SE (LP-2699/LP-2980).
        //
        // Clamped against numberOfPages (matching Pages.tsx's own selection formula exactly) rather than
        // recording the raw currentPage: SubmitCoApplicantWizardDK swaps in a short 2-page list while
        // unauthenticated and a full 7-page CO_SUBMIT list once authenticated, and Pages.tsx exposes the
        // *unclamped* store value via context while rendering whichever child the *clamped* value picks. If
        // this page is ever displayed via that clamp (e.g. a session's access token clears while
        // wizard.currentPage is still deep in the authenticated list, so the short list's last valid index
        // happens to land here), the raw value would record wherever the co-applicant used to be -- possibly
        // CoApplicantSubmitPageDK's own index -- so a later routing-back would land back on Submit instead of
        // here. The clamped value is always this page's actual position in whichever list is currently
        // mounted, since it's the same computation Pages.tsx used to select it as that child.
        dispatch(setGovernmentIdPageIndex(Math.min(Math.max(currentPage, 0), numberOfPages - 1)));
    }, [dispatch, currentPage, numberOfPages]);

    useEffect(() => {
        // Only auto-advance in the direction the co-applicant was already navigating, same as the
        // main-applicant page -- otherwise a still-valid access token would immediately bounce a Back
        // navigation from the next page straight forward again, making Back a no-op.
        //
        // Requiring coapplicantGovernmentId too (not just accessToken) distinguishes "already past this step"
        // from "logged in, but extraction never produced a value" -- MitID's `sub` claim missing, empty, or
        // not DK-prefixed. Without this, once any valid token exists the page would advance regardless of
        // whether extraction ever succeeded. Lower-severity than the main-applicant page (a required,
        // main-applicant-typed value from the invite step usually already fills this field), but the same
        // silent-forever-advance bug applies whenever it doesn't (LP-2980).
        //
        // Also requiring !hasError (matching VerifyIncomePageNO/CoApplicantLogInPage's identical guard):
        // CoApplicantPage's own duplicate-id check (useGovernmentIdUniqueValidation) sets exactly this
        // hasError on this exact field, in the main applicant's own session -- reachable here too if that
        // session's persisted state is ever inherited (e.g. the invite link opened in the same browser
        // tab). Advancing on a flagged-invalid value would reach CoApplicantSubmitPageDK's validation gate,
        // which would just route back here, in the exact same-shape loop the extraction effect below is
        // written to prevent.
        //
        // Also requiring coapplicantCreditStatusConsent (not just accessToken + coapplicantGovernmentId): on
        // `/coapplicant`, the only way to ever obtain a valid accessToken is this page's own `mitid-login`
        // action, which always records that consent first -- so token presence alone was a safe proxy for
        // "consent was given here". On `/ongoing`, the route is already behind its own `Authenticator`, which
        // can hand this page an already-valid accessToken (and, via the extraction effect above, a
        // coapplicantGovernmentId) through a login button that has nothing to do with this page's consent UI.
        // Without this extra check, a co-applicant reaching CO_SUBMIT via `/ongoing` could be auto-advanced
        // straight past the consent step without ever seeing or accepting it.
        const canAutoAdvance =
            !!accessToken &&
            !!coapplicantGovernmentIdFieldState?.value &&
            !coapplicantGovernmentIdFieldState?.hasError &&
            !!coapplicantCreditStatusConsentFieldState?.value;
        if (pageAction === 'back' && canAutoAdvance) {
            defaultPagesAction('back');
        }
        // 'submit-coapplicant' covers CoApplicantSubmitPageDK's own routing back here: PagesActionButton
        // already set pageAction to it, and that's still the value once dispatch(setCurrentPage(...)) lands
        // us back on this page -- `setCurrentPage` only changes which page is shown, not this (separate,
        // local-to-Pages) pageAction state. useDefaultPagesAction only knows how to actually move
        // currentPage for 'next'/'back'/'consolidation-next', so calling it with the original submit-ish
        // action would silently no-op, leaving the co-applicant stuck on this page even after a successful
        // retry supplies a valid id -- with no banner either, since canAutoAdvance being true means
        // governmentIdMissingAfterLogin is false (LP-2980).
        if ((pageAction === 'next' || pageAction === 'submit-coapplicant') && canAutoAdvance) {
            defaultPagesAction('next');
        }
    }, [
        pageAction,
        accessToken,
        coapplicantGovernmentIdFieldState?.value,
        coapplicantGovernmentIdFieldState?.hasError,
        coapplicantCreditStatusConsentFieldState?.value,
    ]);

    // Decode the co-applicant's own government id from the MitID access token's `sub` claim and correct
    // `coapplicantGovernmentId` if it disagrees with what the main applicant typed in on CoApplicantPage at
    // invite time. Compared against the current value rather than only its presence, so this becomes a no-op
    // once the two agree.
    useEffect(() => {
        if (!accessToken) {
            return;
        }
        const governmentId = extractGovernmentIdFromSubClaim(accessToken, CountryCode.DK);
        if (!governmentId) {
            return;
        }
        // Recomputing the duplicate condition rather than blindly clearing a pre-existing hasError: that
        // check is about a relationship between two values (does the co-applicant's id match the main
        // applicant's), not about the coapplicantGovernmentId value being stale by itself. A fresh,
        // successfully authenticated extraction is authoritative for *what the value is*, but it doesn't
        // make the two ids any less equal if they still genuinely are -- so if the authenticated id really
        // does duplicate applicantGovernmentId, that error must survive, not be discarded just because MitID
        // succeeded.
        const isDuplicateOfApplicant = governmentId === applicantGovernmentIdFieldState?.value;
        // Comparing against the recomputed hasError (not just checking whether it's currently set) is what
        // keeps this idempotent: dispatching again every render merely because hasError happens to be true
        // -- rather than because anything is actually about to change -- would create a genuine infinite
        // render loop the moment isDuplicateOfApplicant evaluates to true (dispatch sets hasError: true ->
        // field state object changes reference -> effect re-runs -> still true -> dispatch again, forever).
        if (
            governmentId !== coapplicantGovernmentIdFieldState?.value ||
            !!coapplicantGovernmentIdFieldState?.hasError !== isDuplicateOfApplicant
        ) {
            dispatch(
                addOrUpdateFormField({
                    fieldName: AttributeNames.coapplicantGovernmentId,
                    value: governmentId,
                    hasError: isDuplicateOfApplicant,
                    errorMessage: isDuplicateOfApplicant ? 'validation.governmentid.duplicate' : undefined,
                }),
            );
        }
    }, [accessToken, coapplicantGovernmentIdFieldState, applicantGovernmentIdFieldState]);

    // A valid MitID login exists, but it never yielded (and nothing had already supplied, e.g. from the
    // invite-time CoApplicantPage value) a usable coapplicantGovernmentId -- either the extraction genuinely
    // failed, or it succeeded but left a stale hasError from some other flow unresolved (see the extraction
    // effect above). Recomputed on every render rather than stored, so it clears itself the moment a fresh,
    // successful login supplies a value (LP-2980).
    const governmentIdMissingAfterLogin =
        !!accessToken &&
        (!coapplicantGovernmentIdFieldState?.value || !!coapplicantGovernmentIdFieldState?.hasError);

    const {
        consentDetails,
        isError: isConsentError,
        isLoading: isConsentLoading,
    } = useGetConsentService('TERMS_CREDIT_STATUS');
    // Every consent shown here is mandatory regardless of the `required` flag consent-service attaches --
    // this page exists solely to obtain the eSkat/KreditStatus consent, same as the main-applicant page.
    //
    // `INFORMATION` consents are excluded because `ConsentField` renders them as prose with no checkbox: a
    // name in this list that no input can ever satisfy makes the page permanently impassable.
    const consentsToAccept = consentDetails?.filter((consentDetail) => consentDetail.displayType !== 'INFORMATION');
    const validationErrors = useValidation(consentsToAccept?.map((consentDetail) => consentDetail.name) ?? []);

    // The gate has to be positive: consent is only real if we actually rendered something to consent to.
    // Deriving it from `validationErrors` alone fails OPEN -- an empty `consentsToAccept` (service still
    // loading, service errored, or a 200 carrying no proposals, which is DK's state until LP-2835 lands)
    // leaves nothing to validate, so `validationErrors` would be empty even though nothing was ever shown
    // to consent to. See the note on the dispatch below for why recording one is not harmless.
    const hasConsentToAccept = !isConsentLoading && !isConsentError && !!consentsToAccept?.length;

    const onAction = async (action: WizardPageAction) => {
        if (action === 'mitid-login') {
            if (!hasConsentToAccept) {
                return false;
            }
            if (validationErrors.length) {
                scrollToFieldWithError(validationErrors);
                validationErrors.forEach((validationError) => {
                    dispatch(addOrUpdateFormField(validationError));
                });
                return false;
            }
            // `coapplicantCreditStatusConsent` is not a placeholder waiting on LP-2870 -- it already reaches
            // the credit decision. `dataFormPage` puts it in the co-submit `values`, `LoanApplicantFactory`
            // persists it on the CO applicant (`filterFinancialDetails` drops only `KYC*` keys), and
            // `CreditDecisionRequest.toCustomerInput` deserializes it onto `CustomerInput`, which declares it
            // as a boolean -- so it must be dispatched as one, not the string 'true'. So a value recorded on a
            // path where no consent was shown is a false consent record on a live credit decision -- hence the
            // fail-closed guard above.
            dispatch(addOrUpdateFormField({ fieldName: COAPPLICANT_CREDIT_STATUS_CONSENT, value: true }));
            // On `/ongoing`, the route's own Authenticator may have already supplied a working accessToken
            // and coapplicantGovernmentId before this page ever mounted -- consent was the only thing still
            // missing, so continue directly instead of forcing a redundant second MitID redirect (which would
            // also bounce the co-applicant back to CoApplicantHowToApplyPage, since the wizard resets to page
            // 0 on every mount). A missing/errored governmentId still needs a real (re-)authentication
            // attempt, same as the retry-after-failed-extraction path this mirrors on `/coapplicant`.
            if (
                accessToken &&
                coapplicantGovernmentIdFieldState?.value &&
                !coapplicantGovernmentIdFieldState?.hasError
            ) {
                return defaultPagesAction('next');
            }
            try {
                setError(false);
                await dispatch(initAuthentication());
            } catch {
                setError(true);
            }
            return false;
        }

        return defaultPagesAction(action);
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={defaultPagesAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={coApplicantTotalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.eskat-kreditstatus.title')} />
            <Description text={translate('page.eskat-kreditstatus.description')} />
            {consentDetails?.map((consentDetail) => (
                <ConsentField key={consentDetail.name} consentDetail={consentDetail} />
            ))}
            {/*
             * Shown for a service error AND for a successful response that carried no consent to accept --
             * the latter has no banner of its own, so without this the co-applicant faces a page with nothing
             * to tick and a MitID login button that silently refuses. Matches the main-applicant page.
             */}
            {!isConsentLoading && !hasConsentToAccept && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            {(error || governmentIdMissingAfterLogin) && (
                <ErrorMessage
                    title={translate('page.error.title')}
                    description={translate('page.authentication.submit.log.in.error.description')}
                />
            )}
            <AuthenticationErrorMessage />
            <PagesFooter
                actions={['mitid-login']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={coApplicantTotalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default CoApplicantEskatKreditStatusConsentPageDK;
